import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { queryClient, QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import { companyIdState } from '../company.state';
import { useCompanyFetchMode } from '../company-fetch-context';
import type { EditCompanyContactBodyPayload } from '@/api/v4/organization-contacts.api';
import { editCompanyContacts } from '@/api/v4/organization-contacts.api';
import { useAtomValue } from 'jotai';

export const useEditCompanyContact = () => {
  const { t } = useTranslation('default');
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  const mutation = useMutation({
    mutationFn: editCompanyContacts,
    onError: (error: Error) => {
      notify({
        message: t`unknownError`,
      });
      console.error(error.message);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Company, fetchMode, companyId],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);
    },
  });

  const editContacts = (data: EditCompanyContactBodyPayload) =>
    companyId
      ? mutation.mutateAsync({
          companyId,
          data,
        })
      : undefined;

  return {
    editContacts,
    isError: mutation.isError,
  };
};
